import { StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from 'react-router-dom';

import { AuthProvider } from '@/lib/auth/AuthProvider';
import { initAnalytics } from '@/lib/analytics';
import { captureEmailRedirectArrival } from '@/lib/analytics/emailRedirect';
import { captureLabCheckoutHandoff } from '@/features/labs/labCheckoutStorage';
import { captureMembershipLinks } from '@/features/membership/membershipLinks';
import { queryClient } from '@/lib/query';
import { router } from '@/App';
import { useExperimentDevPanelEnabled } from '@/components/dev/useExperimentDevPanel';
import '@/styles/globals.css';

// Lazy-loaded so the dev panel JS only ships to users who explicitly
// enable it via `?exp-debug=1` or `localStorage.expDebug=1`.
const ExperimentDevPanel = lazy(
  () => import('@/components/dev/ExperimentDevPanel'),
);

function DevPanelMount() {
  const enabled = useExperimentDevPanelEnabled();
  if (!enabled) return null;
  return (
    <Suspense fallback={null}>
      <ExperimentDevPanel />
    </Suspense>
  );
}

captureLabCheckoutHandoff();
captureMembershipLinks();
initAnalytics();
captureEmailRedirectArrival();

const root = document.getElementById('root');
if (!root) throw new Error('#root element missing from index.html');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
        <DevPanelMount />
      </AuthProvider>
      {/* TanStack devtools temporarily hidden during design work.
          Re-enable: change `false &&` back to `import.meta.env.DEV &&`. */}
      {false && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>
);
